/**
 * PageLoader — Barra de progresso no topo, exibida a cada troca de rota.
 *
 * Antes: overlay de tela cheia com animação Lottie e `SHOW_DURATION = 400`.
 * O overlay cobria a página por 400 ms FIXOS em toda navegação, mesmo quando
 * a rota já estava pronta — ou seja, lentidão artificial cobrada em todo clique,
 * além de arrastar o player Lottie para dentro do bundle.
 *
 * Agora: uma barra fina (estilo YouTube/GitHub) que sobe rápido até ~90 %,
 * completa assim que a rota renderiza e some. O conteúdo nunca fica escondido,
 * então a navegação é instantânea — a barra só comunica que algo aconteceu.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/** Tempo até a barra "completar" depois que a rota trocou */
const COMPLETE_DELAY = 260;
/** Duração do fade-out depois de completar */
const FADE_DURATION = 220;

function PageLoader() {
  const location = useLocation();
  const [progresso, setProgresso] = useState(0); // 0 = escondida
  const [saindo, setSaindo] = useState(false);
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    // Mesmo caminho (inclui o double-invoke do StrictMode) → ignora
    if (location.pathname === prevPath.current) return;
    prevPath.current = location.pathname;

    setSaindo(false);
    setProgresso(8);

    // Sobe em dois degraus para dar sensação de carregamento real
    const t1 = setTimeout(() => setProgresso(65), 40);
    const t2 = setTimeout(() => setProgresso(90), 150);
    const t3 = setTimeout(() => setProgresso(100), COMPLETE_DELAY);
    const t4 = setTimeout(() => setSaindo(true), COMPLETE_DELAY + 60);
    const t5 = setTimeout(() => {
      setProgresso(0);
      setSaindo(false);
    }, COMPLETE_DELAY + 60 + FADE_DURATION);

    return () => [t1, t2, t3, t4, t5].forEach(clearTimeout);
  }, [location.pathname]);

  if (progresso === 0) return null;

  return (
    <div
      role="progressbar"
      aria-label="Carregando página"
      aria-hidden="true"
      className="fixed top-0 left-0 right-0 h-[3px] pointer-events-none"
      style={{ zIndex: 10001 }}
    >
      <div
        className="h-full bg-insigne-orange"
        style={{
          width: `${progresso}%`,
          opacity: saindo ? 0 : 1,
          boxShadow: "0 0 10px rgb(var(--c-orange) / 0.8)",
          transition: `width 200ms ease-out, opacity ${FADE_DURATION}ms ease`,
        }}
      />
    </div>
  );
}

export default PageLoader;
